import React, { useState } from 'react';
import DefaultLayout from 'admin/components/DefaultLayout';
import { useIntl } from 'react-intl';
import { Button, Tabs, Table, Dropdown, Input } from 'ui';
import { DownOutlined } from '@ant-design/icons';
import Filter from 'admin/components/Filter';
import { StyledButtonList } from 'styles/buttonList';
import dayjs from 'dayjs';
import useQueryApiClient from 'utils/useQueryApiClient';
import FileDownload from 'js-file-download';
import { MenuProps } from 'antd/es/menu';
import EnumSelect from 'admin/Selects/EnumSelect';
import copyToClipboard from '../../../../utils/copyToClipboard';
import ExportActions from '../../../../enums/farm-user';
import EditUserDrawer from './EditUserDrawer';
import { CreateFarmUserDrawer } from './CreateFarmUserDrawer';
import { Pagination } from 'types/Pagination';
import { UserListStyles } from './style';

type UserRow = {
  id: string;
  name: string;
  email: string;
  role: string;
  created_at?: string;
  updated_at?: string;
};

export interface advancedSearchTypes {
  id?: number;
  full_name?: string;
  email?: string;
  role?: string;
  search?: string;
  page_size?: number;
}

const UserListPage = () => {
  const [advancedSearch, setAdvancedSearch] = useState<advancedSearchTypes>({});
  const [userList, setUserList] = useState<UserRow>();
  const [totalCount, setTotalCount] = useState<Pagination>({});
  const [userEditId, setUserEditId] = useState<number>();
  const [userFullNameEditId, setUserFullNameEditId] = useState<string>();
  const [reload, setReload] = useState<number>(0);
  const [isOpenedUser, setIsOpenedUser] = useState<boolean>(false);

  const intl = useIntl();

  const { appendData: exportData } = useQueryApiClient({
    request: {
      url: `api/v2/users/farms/export`,
      data: {
        filter: advancedSearch,
      },
      multipart: true,
      method: 'POST',
    },
    onSuccess: (response, format) => {
      FileDownload(
        response,
        `${format}_${dayjs().format('YYYY-MM-DD')}.${format}`
      );
    },
  });

  const handlePrint = () => {
    const tableElement = document.getElementById('printTable');
    if (!tableElement) return;

    const paginationElements = Array.from(
      tableElement.getElementsByClassName('ant-pagination')
    );

    paginationElements.forEach((el) => el.remove());

    const printContents = tableElement.innerHTML;

    const newWindow = window.open(' ', ' ', 'width=800,height=600');
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <style>
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #000; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; }
            </style>
          </head>
          <body>
            ${printContents}
          </body>
        </html>
      `);
      newWindow.document.close();
      newWindow.focus();
      newWindow.print();
      newWindow.close();
    }
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'general.user_id' }),
      dataIndex: 'id',
    },
    {
      title: intl.formatMessage({ id: 'farm_users.full_name' }),
      dataIndex: 'name',
    },
    {
      title: intl.formatMessage({ id: 'general.email' }),
      dataIndex: 'email',
    },
    {
      title: intl.formatMessage({ id: 'general.role' }),
      dataIndex: 'role',
    },
    {
      title: intl.formatMessage({ id: 'general.created_at' }),
      dataIndex: 'created_at',
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'general.updated_at' }),
      dataIndex: 'updated_at',
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'general.actions' }),
      dataIndex: 'id',
      render: (id: number, values: any) => (
        <Dropdown
          className="actions"
          overlayClassName="status-actions"
          getPopupContainer={(trigger: any) =>
            trigger.parentNode.querySelector('button') as HTMLElement
          }
          menu={{
            items: [
              {
                label: intl.formatMessage({ id: 'general.edit' }),
                key: 'edit',
              },
            ],
            onClick: (value) => {
              if (value.key == 'edit') {
                setUserEditId(id);
                setUserFullNameEditId(values.name);
                return;
              }
            },
          }}
        >
          <Button
            className="action"
            label={intl.formatMessage({ id: 'general.actions' })}
            icon={<DownOutlined />}
            iconPosition="end"
            size="middle"
            ability={'farm_edit'}
          />
        </Dropdown>
      ),
    },
  ];

  const items = [
    {
      key: 'farm_users',
      label: intl.formatMessage({ id: 'navigation.users' }),
      children: (
        <>
          <Filter
            onFinish={setAdvancedSearch}
            isPagination
            totalRecord={totalCount?.total ?? 0}
          >
            <Input
              label={intl.formatMessage({ id: 'general.user_id' })}
              name="id"
            />
            <Input
              label={intl.formatMessage({ id: 'farm_users.full_name' })}
              name="full_name"
            />
            <Input
              label={intl.formatMessage({ id: 'general.email' })}
              name="email"
            />
            <span className="user-roles">
              <EnumSelect
                label={intl.formatMessage({ id: 'general.role' })}
                name="role"
                code="UserRoles"
              />
            </span>
            <Input
              label={intl.formatMessage({ id: 'general.search' })}
              name="search"
            />
            <Button
              type="primary"
              label={intl.formatMessage({ id: 'general.select' })}
              htmlType="submit"
            />
          </Filter>
          <div id={'printTable'}>
            <UserListStyles>
              <Table
                url="/api/v2/users"
                columns={columns}
                filter={advancedSearch}
                page_size={advancedSearch.page_size ?? 25}
                paginnationStatus={setTotalCount}
                reload={reload}
                saveData={(el: UserRow) => {
                  setUserList(el);
                }}
              />
            </UserListStyles>
          </div>
        </>
      ),
    },
  ];

  const headers = [
    intl.formatMessage({ id: 'general.user_id' }),
    intl.formatMessage({ id: 'farm_users.full_name' }),
    intl.formatMessage({ id: 'general.email' }),
    intl.formatMessage({ id: 'general.role' }),
    intl.formatMessage({ id: 'general.created_at' }),
    intl.formatMessage({ id: 'general.updated_at' }),
  ].join('\t');

  const formatRow = (row: UserRow) => {
    return [
      row.id || '',
      row.name || '',
      row.email || '',
      row.role || '',
      row.created_at ? dayjs(row.created_at).format('DD.MM.YYYY') : '',
      row.updated_at ? dayjs(row.updated_at).format('DD.MM.YYYY') : '',
    ].join('\t');
  };

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case ExportActions.XLSX:
      case ExportActions.CSV:
      case ExportActions.PDF:
        exportData({ format: e.key, filter: advancedSearch }, [], e.key);
        break;
      case ExportActions.PRINT:
        handlePrint();
        break;
      case ExportActions.COPY:
        copyToClipboard({
          data: userList,
          headers: headers,
          formatRow: formatRow,
          intl: intl,
        });
        break;
    }
  };

  const dropdownItems: MenuProps['items'] = [
    {
      label: intl.formatMessage({ id: 'general.print' }),
      key: 'print',
    },
    {
      label: intl.formatMessage({ id: 'general.copy' }),
      key: 'copy',
    },
    {
      label: intl.formatMessage({ id: 'general.excel' }),
      key: 'xlsx',
    },
    {
      label: intl.formatMessage({ id: 'general.csv' }),
      key: 'csv',
    },
  ];

  const menuProps = {
    items: dropdownItems,
    onClick: handleMenuClick,
  };

  return (
    <DefaultLayout.PageLayout
      breadcrumbs={[
        {
          title: intl.formatMessage({ id: 'navigation.beginning' }),
        },
        {
          title: intl.formatMessage({ id: 'navigation.users' }),
        },
      ]}
    >
      <EditUserDrawer
        userEditId={userEditId}
        setUserEditId={setUserEditId}
        fullName={userFullNameEditId}
        reload={reload}
        setReload={setReload}
      />
      <CreateFarmUserDrawer
        isOpen={isOpenedUser}
        setIsOpen={setIsOpenedUser}
        reload={reload}
        setReload={setReload}
      />
      <DefaultLayout.PageContent>
        <Tabs
          tabBarExtraContent={
            <StyledButtonList>
              <Button
                type="primary"
                label={intl.formatMessage({ id: 'admin.add_new' })}
                htmlType="submit"
                onClick={() => setIsOpenedUser(true)}
              />
              <Dropdown menu={menuProps}>
                <Button
                  label={intl.formatMessage({ id: 'general.export' })}
                  type="primary"
                />
              </Dropdown>
            </StyledButtonList>
          }
          items={items}
        />
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default UserListPage;
